import { useEffect, useRef, useState } from "react";
import { Alert, AlertIcon, Box, Fade, Flex } from "@chakra-ui/react";
import FileUploadProgress from "../../../dsr-create-post/FileUploadProgress";
import config from "../../../../../config/app-config";
import LaunchdeckButton from "../../../../global/components/helper-components/components/LaunchdeckButton";
import { BsSoundwave } from 'react-icons/bs';
import LaunchdeckAudioPlayer from "../../../../global/components/helper-components/components/LaunchdeckAudioPlayer";
import AudioRecorderModule from "../sub-widget-sub-components/AudioRecorderModule";
import { getTimestampForFileName } from "../../../../global/helpers/time-format-helpers";
import PlainFormTextInput from "../../../../forms/TextInputField/components/PlainFormTextInput";

export const UploadedAudio = ({
  dsrId,
  editableItemId,
  subWidgetId,
  setSubWidgetContentAndRef,
  subWidgetContent,
  uploadFiles,
  uploadFileMultithreaded,
  uploadFilesState,
  uploadFilesReset,
  // handleSaveItemByClickOutside,
  subWidgetAutoSave,
  isDeleting,
  isDragging,
  handleEditItemByClick,
}) => {
  const initRef = useRef(false);
  const containerRef = useRef(null);

  const [audioFileName, setAudioFileName] = useState("")
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadResults, setUploadResults] = useState("")

  const [fileNameChanged, setFileNameChanged] = useState(false)
  const [showLoadingState, setShowLoadingState] = useState(false);
  const [isAudioRecord, setIsAudioRecord] = useState(true)
  const [processingSubwidgetId, setProcessingSubwidgetId] = useState("")
  const {
    maxVideoFileSize,
    minVideoFileSizeForMultithreadedUpload,
    displayMaxFileSize,
  } = config;

  useEffect(()=> {
    if (uploadFilesState) {
      const { results: uploadResults, error: uploadingError } = uploadFilesState;

      if (uploadResults) {
        setUploadResults(uploadResults)
      }

      if (uploadingError) {
        setErrorMessage(uploadingError)
        setShowLoadingState(false)
      }
    }
  },[uploadFilesState])

  useEffect(() => {
    // Save uploaded file response to sub-widget content
    if (
      !errorMessage &&
      Array.isArray(uploadResults?.files) &&
      uploadResults?.files.length > 0 &&
      uploadResults?.files[0]?.subWidgetId === subWidgetId
    ) {
      setSubWidgetContentAndRef(uploadResults.files); 
      setProcessingSubwidgetId("")
      uploadFilesReset();
      setShowLoadingState(false)
    }
  }, [uploadResults]);


  useEffect(() => {
    //Auto save sub-widget content to local state after file uploading
      if (!showLoadingState && !errorMessage && subWidgetContent[0]?.key
        && editableItemId === subWidgetId // Review if this is needed?
        && uploadResults?.files
      ) {
        subWidgetAutoSave();
      }
  
      if (subWidgetContent && !initRef.current) {
        const { fileName } = subWidgetContent[0] || {}
        const audioFileName = fileName ? fileName : "My-Recording-" + getTimestampForFileName();
  
        setAudioFileName(audioFileName)
        initRef.current = true
      }
  }, [subWidgetContent, editableItemId]);


  const validateInput = (file) => {
    if (!file) {
      throw new Error("UploadedVideo must be object");
    }

    if (!file?.name) {
      throw new Error("UploadedVideo does not have filename");
    }

    if (file?.size > maxVideoFileSize) {
      // File size exceeds the limit
      setErrorMessage(
        `We support the display of video files that are ${displayMaxFileSize / (1024 * 1024)} MB or below.`,
      );
      throw new Error("File size exceeds the limit");
    }
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files[0];

    try {
      validateInput(file);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.log(e);

      return;
    }
    setProcessingSubwidgetId(subWidgetId)
    file?.size <= minVideoFileSizeForMultithreadedUpload
      ? uploadFiles({ dsrId: dsrId, files: [file], subWidgetId: subWidgetId, fileName: file?.name, containsImages: false })
      : uploadFileMultithreaded({ dsrId, file, subWidgetId, fileName: file?.name, containsImages: false });
    setShowLoadingState(true)
  };

  const handleChangeAudioFileName = (event) => {
    const audioFileName = event.target.value;
  
    setAudioFileName(audioFileName); 
    if (!fileNameChanged) setFileNameChanged(true);

  }

  const handleUploadRecordedAudioFile = (file) => {
    setProcessingSubwidgetId(subWidgetId)

    if (file) {
      file?.size <= minVideoFileSizeForMultithreadedUpload
        ? uploadFiles({ dsrId: dsrId, files: [file], subWidgetId: subWidgetId, fileName: "My Recording", containsImages: false })
        : uploadFileMultithreaded({ dsrId, file, subWidgetId, fileName: "My Recording", containsImages: false });
      setShowLoadingState(true)
    }
    
  }

  const handleChangeToUploadMode = () => {
    setIsAudioRecord(false)
  }

  const handleUpdateFileName = () => {
    if (fileNameChanged && audioFileName.trim()) {
      const updatedContent = [{
        ...subWidgetContent[0],
        fileName: audioFileName.trim()
      }];

      setSubWidgetContentAndRef(updatedContent)
      subWidgetAutoSave();
    }
  }

  const handleClick = () => {
    if (!isDragging) {
      handleEditItemByClick()
    }
  }

  const onPressEnterAndLogin = (e) => {
		if (e.key === 'Enter') {
			handleUpdateFileName();
		}
	}
  
  return subWidgetContent ? (
    <>
    {isDeleting ? 
     <Flex w='100%' justify='center' align='center' mr='3.5em' mt='2em' minH='8em'>
      <Box className="removing-subwidget" /> 
      </Flex>
    :
    <>
      <Flex w='100%' onClick={handleClick} onKeyDown={(e) => onPressEnterAndLogin(e)}>
        <PlainFormTextInput
          name="audio-file-name"
          htmlFor='audio-file-name'
          label=""
          placeholder=""
          mb='0.2em'
          value={audioFileName}
          onChange={(event) => handleChangeAudioFileName(event)}
          cursor='pointer'
          w='50%'
          minW='25em'
          unfocusedNoBorder={true}
          onBlur={()=> handleUpdateFileName()}
        />
      </Flex>
      
      <LaunchdeckAudioPlayer
        autoPlay={false}
        file={subWidgetContent[0]}
        showFileName={false}
      />
</>
}
    </>
  ) : (
    <Box w='100%' ref={containerRef} className="audio-uploader">
      <Flex direction='column' w='100%'>
      
        {showLoadingState && (processingSubwidgetId === subWidgetId) ? (
          <Flex w="100%" py="2em" justify="center">
            <FileUploadProgress showLoader={showLoadingState} subWidgetId={subWidgetId}/>
          </Flex>
        ) :
        <>
          {isAudioRecord &&
          <Fade
            in={isAudioRecord}
            transition={{ duration: 0.5 }}
          >
            <Flex className="video-preview" direction="column" align="center">
          

              <AudioRecorderModule
                handleUploadRecordedAudioFile={handleUploadRecordedAudioFile}
                handleChangeToUploadMode={handleChangeToUploadMode}
              />
          
            </Flex>
          </Fade>
        }

        {!isAudioRecord &&
          <Fade
            in={!isAudioRecord}
            transition={{ duration: 0.5 }}
          >

            {(!showLoadingState || (processingSubwidgetId !== subWidgetId)) && (
              <Flex
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                minHeight="12.5em"
                fontSize="0.875em"
              >
                <Box>
                  <Box
                    fill="brandDark.500"
                    as={BsSoundwave}
                    width="48px"
                    height="3.5em"
                    my="2em"
                  />
                </Box>
                <div className="video-actions">
                  <Flex direction='column' gap={2}>
                    <label>
                      <input
                        style={{ display: "none" }}
                        type="file"
                        accept="audio/mpeg,audio/wav,audio/ogg,audio/webm"
                        onChange={handleFileInputChange}
                      />
                      <LaunchdeckButton
                        bg="brandDark.500"
                        color="white"
                        px="2em"
                        label="Upload audio file"
                      />
                    </label>


                  </Flex>
                </div>

                <Flex
                  direction="column"
                  my="1.5em"
                  fontSize="0.875em"
                  color="brandDark.500"
                  justify="center"
                  align="center"
                >
                  <Box mt="0.75em" color="gray.500" textAlign="center">
                    Supported Formats
                  </Box>
                  <Box textAlign="center">MP3, WAV, WEBM, and OGG</Box>
                  <Box mt="0.8em" textAlign="center">
                    <Box color="gray.500">Upload limit</Box>
                    200MB
                  </Box>
                </Flex>

                <LaunchdeckButton
                  bg="none"
                  color="gray.500"
                  label="< Record voice message"
                  fontSize='0.75em'
                  border='none'
                  onClick={() => setIsAudioRecord(true)}
                />
              </Flex>
            )}

            {errorMessage && (
              <Box justifyContent="center" mt="15px" mb="15px" px="1em">
                <Alert
                  status={"error"}
                  variant="solid"
                  px="1em"
                  bg="pink.50"
                  rounded="0.25em"
                  border="1px solid"
                  borderColor="brandPink.500"
                >
                  <AlertIcon boxSize={10} color="brandPink.500" />
                  <Box fontSize="0.875em" color="brandDark.500" ml="1em" py="1em">
                    <Box fontWeight="600">
                      The current upload limit is {displayMaxFileSize / (1024 * 1024)}{" "}
                      MB
                    </Box>
                    <Box mt="0.5em">
                      For larger video files, we recommend embedding Youtube videos or
                      provide Dropbox/Google drive links instead{" "}
                    </Box>
                  </Box>
                </Alert>
              </Box>
            )}
          </Fade>
        }
        </>
        } 
      </Flex>
    </Box>
  );
};
